import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import { Container } from '@edx/paragon';
import ExamStateContext from '../../../context';
import { ExamStatus } from '../../../constants';
import WarningModal from '../WarningModal';
import { pollExamAttempt, softwareDownloadAttempt } from '../../../data/api';
import messages from '../messages';
import ProviderInstructions from './ProviderInstructions';
import DefaultInstructions from './DefaultInstructions';
import DownloadButtons from './DownloadButtons';
import Footer from '../Footer';
import SkipProctoredExamButton from '../SkipProctoredExamButton';
import {getAuthenticatedUser} from "@edx/frontend-platform/auth";

const DownloadSoftwareProctoredExamInstructions = ({ intl, skipProctoredExam }) => {
  const state = useContext(ExamStateContext);
  const {
    proctoringSettings,
    exam,
    getExamAttemptsData,
    allowProctoringOptOut,
  } = state;
  const {
    attempt,
    course_id: courseId,
    content_id: sequenceId,
  } = exam;
  const {
    exam_started_poll_url: pollUrl,
    attempt_code: examCode,
    attempt_id: attemptId,
    software_download_url: downloadUrl,
  } = attempt;
  const {
    provider_tech_support_email: supportEmail,
    provider_tech_support_phone: supportPhone,
    exam_proctoring_backend: proctoringBackend,
  } = proctoringSettings;
  const { instructions } = proctoringBackend || {};
  const [systemCheckStatus, setSystemCheckStatus] = useState('');
  const [downloadClicked, setDownloadClicked] = useState(false);
  const withProviderInstructions = instructions && instructions.length > 0;

  const handleDownloadClick = () => {
    pollExamAttempt(`${pollUrl}?sourceid=instructions`)
      .then((data) => {
        if (data.status === ExamStatus.READY_TO_START) {
          setSystemCheckStatus('success');
        } else {
          softwareDownloadAttempt(attemptId);
          if (exam.extended_params.service !== 'EXAMUS') { window.open(downloadUrl, '_blank'); } else { window.open(downloadUrl, '_self'); }
        }
      });
    setDownloadClicked(true);
  };

  const handleStartExamClick = () => {
    pollExamAttempt(`${attempt.exam_started_poll_url}?sourceid=instructions`)
      .then((data) => (
        data.status === ExamStatus.READY_TO_START
          ? getExamAttemptsData(courseId, sequenceId)
          : setSystemCheckStatus('failure')
      ));
  };

  return (
    <div>
      <Container className="border py-5 mb-4">
        <WarningModal
          isOpen={Boolean(systemCheckStatus)}
          title={
            systemCheckStatus === 'success'
              ? intl.formatMessage(messages.softwareLoadedModalTitle)
              : intl.formatMessage(messages.cannotStartModalTitle)
          }
          body={
            systemCheckStatus === 'success'
              ? intl.formatMessage(messages.softwareLoadedModalBody)
              : intl.formatMessage(messages.cannotStartModalBody)
          }
          handleClose={() => setSystemCheckStatus('')}
        />
        <div className="h3" data-testid="exam-instructions-title">
          <FormattedMessage
            id="exam.DownloadSoftwareProctoredExamInstructions.title"
            defaultMessage="Set up and start your proctored exam."
          />
        </div>
        {exam.extended_params.service === 'ITMO' && getAuthenticatedUser()
          && (
          <div>
            <FormattedMessage
              id="exam.AccountWarning"
              defaultMessage="Make sure that you are using your account for this exam: "
            />
            <b>{getAuthenticatedUser().username}</b>
          </div>
          )}
        {withProviderInstructions
          ? (
            <ProviderInstructions
              providerName={exam.extended_params.service}
              supportEmail={supportEmail}
              supportPhone={supportPhone}
              instructions={instructions}
            />
          )
          : (
            <DefaultInstructions
              code={examCode}
              providerName={exam.extended_params.service}
            />
          )}
        <DownloadButtons
          downloadUrl={downloadUrl}
          onDownloadClick={handleDownloadClick}
          onStartExamClick={handleStartExamClick}
          downloadClicked={downloadClicked}
          providerName={exam.extended_params.service}
        />
      </Container>
      {allowProctoringOptOut && <SkipProctoredExamButton handleClick={skipProctoredExam} />}
      <Footer />
    </div>
  );
};

DownloadSoftwareProctoredExamInstructions.propTypes = {
  intl: intlShape.isRequired,
  skipProctoredExam: PropTypes.func.isRequired,
};

export default injectIntl(DownloadSoftwareProctoredExamInstructions);
